'use client';

import { Button } from '@nextui-org/react';
import EmojiPicker from 'emoji-picker-react';
import React, { useRef, useState } from 'react';

import { IconElements, IconFilter, IconSendMessage } from '@/assets';
import MentionUser from '@/components/mention-user';
import { useAuth } from '@/provider/UserProvider';

import useGroupChat from './useGroupChat';
import PopoverCustom from '@/components/popover-custom';
import OptionText2 from '@/contents/gen-model/sideBar/custom/OptionText2';
import { UseFormReturn, FieldValues } from 'react-hook-form';
import { sizeOfImageChatGenerator } from '@/configs/constant';
import { generateTypeChatGenerator } from '@/configs/optionsModel';
import useGenerativeImage from '@/app/chat/group-chat/useGenerativeImage';
import { toast } from 'react-toastify';

const SendMessage: React.FC<{
  callback?: () => void;
  setContent: React.Dispatch<React.SetStateAction<string>>;
  content: string;
  setListCommandDisplay: React.Dispatch<React.SetStateAction<any[] | undefined>>;
  listCommand: any[] | undefined;
  setSelectMethodCommandLine: React.Dispatch<any>;
  refUseForm: UseFormReturn<FieldValues, any, undefined>;
  selectMethodCommandLine: any;
}> = ({
  callback,
  setContent,
  content,
  setListCommandDisplay,
  listCommand,
  selectMethodCommandLine,
  setSelectMethodCommandLine,
  refUseForm,
}) => {
  const [listMention, setListMention] = useState<any[]>([]);
  const [isOpenEmoji, setIsOpenEmoji] = useState(false);
  const [isOpenList, setIsOpenList] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const { isLogin } = useAuth();
  const { onPostMessage } = useGroupChat();
  const onFinish = () => {
    setContent('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
    callback && callback();
  };
  const { generateImage } = useGenerativeImage(
    selectMethodCommandLine,
    setSelectMethodCommandLine,
    onFinish,
  );
  const { getValues, register, watch, setValue: setValueForm } = refUseForm;

  const replaceMentionsWithIds = (content: string): string => {
    return listMention.reduce((acc, user) => {
      const tag = `@${user.name}`;
      const replacement = `<@${user.id}>`;
      return acc.split(tag).join(replacement);
    }, content);
  };

  const onSubmit = () => {
    if (content && content.length >= 500) {
      return toast.error('The chat content must be less than 500 characters.');
    }
    if (selectMethodCommandLine) {
      generateImage({ ...getValues(), prompt: content });
    } else {
      const contentSubmit: string = replaceMentionsWithIds(content);
      onPostMessage({ content: contentSubmit, callback: onFinish });
      setIsOpenEmoji(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setSelectMethodCommandLine(null);
    }
    if (Array.from(e.target.value)[0] === '/') {
      if (
        !listCommand?.find(
          (command) =>
            e.target.value?.toLocaleLowerCase()?.includes(command?.command?.toLocaleLowerCase()),
        )
      ) {
        setSelectMethodCommandLine(null);
      }
      listCommand?.forEach((item) => {
        if (
          item?.command?.toLocaleLowerCase() === e.target.value?.toLocaleLowerCase().slice(0, 10)
        ) {
          setSelectMethodCommandLine(item);
        }
      });
      setListCommandDisplay(
        listCommand?.filter(
          (command) =>
            command?.command?.toLocaleLowerCase()?.includes(e.target.value?.toLocaleLowerCase()),
        ),
      );
    }
    setContent(e.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const caretPosition = input.selectionStart;
    const currentValue = input.value;

    if (event.key === 'Enter') {
      onSubmit();
    }
    if (event.key === 'Escape' && isOpenList) {
      setIsOpenList(false);
    }
    if (event.key === ' ' && isOpenList) {
      setIsOpenList(false);
    }
    if (event.key === '@') {
      setIsOpenList(true);
    }
    if (event.key === 'Backspace' && isOpenList) {
      if (caretPosition !== null && caretPosition > 0 && currentValue[caretPosition - 1] === '@') {
        setIsOpenList(false);
      }
    }
  };

  return (
    <>
      <div className="relative flex items-center justify-between">
        {isOpenEmoji && (
          <EmojiPicker
            style={{ position: 'absolute', top: '-520px', zIndex: 20 }}
            height={500}
            width={400}
            onEmojiClick={(e) => {
              setContent((prevContent) => prevContent + e.emoji);
            }}
          />
        )}
        <Button
          isIconOnly
          variant="light"
          className="p-2 md:hidden"
          onClick={() => setIsOpenEmoji(!isOpenEmoji)}
        >
          <IconElements />
        </Button>

        <div aria-checked={isLogin} className="w-[100%]">
          <input
            name="message"
            ref={inputRef}
            value={content}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            className={`h-[40px] w-full border-none bg-white px-3 text-[14px] outline-none ${
              selectMethodCommandLine ? 'border !border-double rounded-[10px] border-primary' : ''
            }`}
            placeholder="Type / to display command E.g. /promptSD type prompt here"
          />
        </div>
        <PopoverCustom
          trigger={<IconFilter className="mr-3 cursor-pointer" />}
          content={
            <form
              style={{ filter: 'drop-shadow(0px 8px 25px rgba(126, 126, 126, 0.25))' }}
              className="p-3 w-[345px] bg-white rounded-xl rounded-br-none relative"
            >
              <div className="absolute -bottom-[20px] -right-[2px] h-0 w-0 translate-x-[0.2px] -rotate-[45deg] border-y-[15px] border-l-[15px] border-transparent border-l-white" />
              <p className="text-16 font-semibold mb-3">Settings</p>
              {generateTypeChatGenerator.chatGeneratorOption?.options.map((item: any, index) => (
                <OptionText2
                  key={index}
                  getValues={getValues}
                  item={item}
                  register={register}
                  watch={watch}
                  setValue={setValueForm}
                  borderLess={item.borderLess}
                  sizeModel={sizeOfImageChatGenerator}
                  borderLessMarginTop
                />
              ))}
            </form>
          }
          position="top-right"
          contentClassName="-translate-x-[30px] -translate-y-[15px] sm:-translate-y-[10px]"
          onOpenChange={(aaa) => console.log(aaa)}
        />
        <div
          onClick={onSubmit}
          className={`rounded-xl p-2 hover:cursor-pointer ${
            content ? 'bg-[#DA613A]' : 'bg-gray-200'
          }`}
        >
          <IconSendMessage />
        </div>
        <MentionUser
          content={content}
          isOpenList={isOpenList}
          setContent={setContent}
          setIsOpenList={setIsOpenList}
          inputRef={inputRef}
          setListMention={setListMention}
          isRoomChat
        />
      </div>
    </>
  );
};

export default SendMessage;
